export interface TranslationOptions {
    injectData?: Map<string, string>;
    elComputation?: boolean;
    tags?: string[];
}

/**
 * Translation utils class that handles contextual translations for complex strings
 *
 * @author Upamanyu a0230288
 */
export class TranslationUtils {

    /**
     * default options for the translation method
     */
    private static _defaultTranslationOptions: TranslationOptions = {
        tags: ['a'],
        elComputation: true,
        injectData: null
    };

    /**
     * method to succesfully generate part-wise translated texts from the single text based on <a> and </a>
     * assumes that the actual text doesn't have <a> and </a> in it
     * Also takes care of injecting data from a map assuming each key occurs exactly once in the text
     *
     * @param translatedFullText - the full translated text
     * @param opts - translation options
     */
    public static deconstructComplexTranslations(translatedFullText: string, opts?: TranslationOptions): string[] {
        const actualOpts: TranslationOptions = {
            tags: opts?.tags ?? this._defaultTranslationOptions.tags,
            elComputation: opts?.elComputation ?? this._defaultTranslationOptions.elComputation,
            injectData: opts?.injectData ?? this._defaultTranslationOptions.injectData
        };

        // do the link computation before inject so that no chance of random characters from dynamic variables can mess it up
        let partwiseTexts: string[] = [];
        if (actualOpts.elComputation) {
            translatedFullText = this._sanitizeTags(translatedFullText, actualOpts.tags);
            translatedFullText
                .split(`</${actualOpts.tags[0]}>`)
                .forEach(part => {
                    partwiseTexts.push(...part.split(`<${actualOpts.tags[0]}>`).filter(subpart => subpart.length > 0));
                });
        } else {
            partwiseTexts.push(translatedFullText);
        }

        // inject data
        if (actualOpts.injectData) {
            const newPartWiseTexts: string[] = [];

            partwiseTexts.forEach((part: string) => {
                let keyExists = false;
                actualOpts.injectData.forEach((value: string, key: string) => {
                    if (part.includes(key)) {
                        keyExists = true;
                        newPartWiseTexts.push(part.replace(key, value));
                    }
                });
                if (!keyExists) {
                    newPartWiseTexts.push(part);
                }
            });
            partwiseTexts = newPartWiseTexts;
        }

        return partwiseTexts;
    }

    /**
     * Sanitize the tags so that all allowed start and end tags are replaced by the first one only.
     * This is done for easier processing.
     */
    private static _sanitizeTags(translatedFullText: string, tags: string[]) {
        tags.forEach((tag, index) => {
            if (index !== 0 || tag !== tags[0]) {
                const startTagsReg = new RegExp(`<${tag}>`, 'g');
                const endTagsReg = new RegExp(`</${tag}>`, 'g');
                translatedFullText = translatedFullText
                    .replace(startTagsReg, `<${tags[0]}>`)
                    .replace(endTagsReg, `</${tags[0]}>`);
            }
        });
        return translatedFullText;
    }

}
